import React, { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Plus, Pencil, Trash2, LogOut, Mail, FolderKanban, Loader2, X, CheckCircle2, Upload, Image as ImageIcon, FileText, ExternalLink, Languages } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import Logo from "../components/Logo";
import { useI18n } from "../i18n";
import ContentManager from "../components/ContentManager";
import { MediaLibrary } from "../components/MediaLibrary";
import ProjectTranslations from "../components/ProjectTranslations";

const CATEGORIES = ["Konut", "Ticari", "Fabrika", "Lojistik", "Proje Danışmanlıkları", "Mimari Tasarım & Dekorasyon"];
const STATUSES = ["Tamamlanan", "Devam Eden", "Hedef", "Tadilat"];

const EMPTY = {
  title: "", title_en: "", category: CATEGORIES[0], status: STATUSES[0], location: "",
  description: "", description_en: "", image_url: "", gallery: [], youtube_urls: [], year: "", featured: false,
};

export default function AdminDashboard() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [tab, setTab] = useState("projects");
  const [projects, setProjects] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | project obj | {new:true}
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const coverInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  // Hero slaytları
  const [heroSlides, setHeroSlides] = useState([]);
  const [heroSaving, setHeroSaving] = useState(false);
  const heroUploadRef = useRef(null);
  const [reordering, setReordering] = useState(false);
  const [libTarget, setLibTarget] = useState(null); // 'cover' | 'gallery'

  const onLibrarySelect = (urls) => {
    if (libTarget === "cover") {
      setForm((f) => ({ ...f, image_url: urls[0] }));
    } else {
      setForm((f) => {
        const existing = String(f.gallery || "").trim();
        return { ...f, gallery: (existing ? existing + "\n" : "") + urls.join("\n") };
      });
    }
    setLibTarget(null);
  };

  const moveProject = async (idx, dir) => {
    if (reordering) return;
    const j = idx + dir;
    if (j < 0 || j >= projects.length) return;
    const arr = [...projects];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setProjects(arr);
    setReordering(true);
    try {
      await api.put("/admin/projects/reorder", { order: arr.map((p) => p.id) });
    } catch (e) {
      toast.error("Sıralama kaydedilemedi");
      load();
    } finally {
      setReordering(false);
    }
  };

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return res.data.url;
  };

  const onUploadCover = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setForm((f) => ({ ...f, image_url: url }));
      toast.success("Kapak görseli yüklendi");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Yükleme başarısız");
    } finally {
      setUploading(false);
      if (coverInputRef.current) coverInputRef.current.value = "";
    }
  };

  const onUploadGallery = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const url = await uploadFile(f);
        urls.push(url);
      }
      setForm((f) => {
        const existing = String(f.gallery || "").trim();
        const next = (existing ? existing + "\n" : "") + urls.join("\n");
        return { ...f, gallery: next };
      });
      toast.success(`${urls.length} görsel galeriye eklendi`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Yükleme başarısız");
    } finally {
      setUploading(false);
      if (galleryInputRef.current) galleryInputRef.current.value = "";
    }
  };

  const adminEmail = typeof window !== "undefined" ? localStorage.getItem("demiray_admin_email") : "";

  const load = async () => {
    setLoading(true);
    try {
      const [p, m, h] = await Promise.all([
        api.get("/projects"),
        api.get("/admin/messages"),
        api.get("/settings/hero"),
      ]);
      setProjects(p.data);
      setMessages(m.data);
      setHeroSlides(h.data?.slides || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("demiray_token");
        navigate("/admin/login");
      } else {
        toast.error("Veri yüklenemedi");
      }
    } finally {
      setLoading(false);
    }
  };

  // Hero slayt yönetimi
  const addHeroSlide = () => setHeroSlides((s) => [...s, ""]);
  const removeHeroSlide = (i) => setHeroSlides((s) => s.filter((_, idx) => idx !== i));
  const updateHeroSlide = (i, val) => setHeroSlides((s) => s.map((v, idx) => idx === i ? val : v));
  const moveSlide = (i, dir) => {
    setHeroSlides((s) => {
      const arr = [...s];
      const j = i + dir;
      if (j < 0 || j >= arr.length) return arr;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return arr;
    });
  };
  const uploadHeroSlide = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const url = await uploadFile(f);
        setHeroSlides((s) => [...s, url]);
      }
      toast.success(`${files.length} slayt eklendi`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Yükleme başarısız");
    } finally {
      setUploading(false);
      if (heroUploadRef.current) heroUploadRef.current.value = "";
    }
  };
  const saveHero = async () => {
    const filtered = heroSlides.map((s) => (s || "").trim()).filter(Boolean);
    if (!filtered.length) { toast.error("En az 1 slayt olmalı"); return; }
    setHeroSaving(true);
    try {
      await api.put("/admin/settings/hero", { slides: filtered });
      setHeroSlides(filtered);
      toast.success("Hero slaytları kaydedildi");
    } catch (err) {
      toast.error("Kaydedilemedi");
    } finally {
      setHeroSaving(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("demiray_token")) {
      navigate("/admin/login");
      return;
    }
    load();
  }, []);

  const logout = () => {
    localStorage.removeItem("demiray_token");
    localStorage.removeItem("demiray_admin_email");
    navigate("/admin/login");
  };

  const openNew = () => { setEditing({ new: true }); setForm(EMPTY); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      ...EMPTY,
      ...p,
      gallery: Array.isArray(p.gallery) ? p.gallery.join("\n") : (p.gallery || ""),
      youtube_urls: Array.isArray(p.youtube_urls) ? p.youtube_urls.join("\n") : (p.youtube_urls || ""),
    });
  };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const galleryArr = Array.isArray(form.gallery)
        ? form.gallery
        : String(form.gallery || "").split("\n").map((s) => s.trim()).filter(Boolean);
      const youtubeArr = Array.isArray(form.youtube_urls)
        ? form.youtube_urls
        : String(form.youtube_urls || "").split("\n").map((s) => s.trim()).filter(Boolean);
      const payload = {
        title: form.title,
        title_en: form.title_en || "",
        title_i18n: form.title_i18n || {},
        category: form.category,
        status: form.status || "Tamamlanan",
        location: form.location,
        description: form.description || "",
        description_en: form.description_en || "",
        image_url: form.image_url,
        gallery: galleryArr,
        youtube_urls: youtubeArr,
        year: form.year || "",
        featured: !!form.featured,
      };
      if (editing?.new) {
        await api.post("/projects", payload);
        toast.success("Proje eklendi");
      } else {
        await api.put(`/projects/${editing.id}`, payload);
        toast.success("Proje güncellendi");
      }
      close();
      load();
    } catch (e) {
      toast.error("Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (p) => {
    if (!window.confirm(`"${p.title}" silinsin mi?`)) return;
    try {
      await api.delete(`/projects/${p.id}`);
      toast.success("Silindi");
      load();
    } catch (e) {
      toast.error("Silinemedi");
    }
  };

  const onMarkRead = async (m) => {
    try {
      await api.put(`/admin/messages/${m.id}/read`);
      load();
    } catch (e) { toast.error("Hata"); }
  };
  const onDeleteMsg = async (m) => {
    if (!window.confirm("Mesaj silinsin mi?")) return;
    try {
      await api.delete(`/admin/messages/${m.id}`);
      toast.success("Silindi");
      load();
    } catch (e) { toast.error("Silinemedi"); }
  };

  const unreadCount = messages.filter((m) => !m.read).length;

  return (
    <div className="min-h-screen bg-[var(--bg)]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="border-b border-[var(--border)] bg-[var(--surface)]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/"><Logo size={32} withText={false} /></Link>
            <div className="hidden md:block text-xs tracking-widest uppercase text-[var(--text-muted)]">
              CMS · {t.admin.dashboard}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="hidden sm:flex items-center gap-2 px-3 py-2 border border-[var(--border)] text-xs uppercase tracking-widest text-white hover:border-white"
              data-testid="admin-view-site"
              title="Siteyi yeni sekmede aç"
            >
              <ExternalLink size={14} />
              <span className="hidden md:inline">Siteyi Görüntüle</span>
            </a>
            <span className="hidden sm:block text-xs text-[var(--text-muted)]">{adminEmail}</span>
            <button
              onClick={logout}
              className="flex items-center gap-2 px-3 py-2 border border-[var(--border)] text-xs uppercase tracking-widest text-white hover:border-white"
              data-testid="admin-logout"
            >
              <LogOut size={14} />
              {t.admin.logout}
            </button>
          </div>
        </div>
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 flex gap-px bg-[var(--border)]">
          <button
            onClick={() => setTab("projects")}
            data-testid="tab-projects"
            className={`px-4 md:px-6 py-3 text-xs uppercase tracking-widest flex items-center gap-2 ${
              tab === "projects" ? "bg-[var(--bg)] text-white" : "bg-[var(--surface)] text-[var(--text-muted)] hover:text-white"
            }`}
          >
            <FolderKanban size={14} /> Projeler ({projects.length})
          </button>
          <button
            onClick={() => setTab("hero")}
            data-testid="tab-hero"
            className={`px-4 md:px-6 py-3 text-xs uppercase tracking-widest flex items-center gap-2 ${
              tab === "hero" ? "bg-[var(--bg)] text-white" : "bg-[var(--surface)] text-[var(--text-muted)] hover:text-white"
            }`}
          >
            <ImageIcon size={14} /> Anasayfa Görselleri ({heroSlides.length})
          </button>
          <button
            onClick={() => setTab("messages")}
            data-testid="tab-messages"
            className={`px-4 md:px-6 py-3 text-xs uppercase tracking-widest flex items-center gap-2 ${
              tab === "messages" ? "bg-[var(--bg)] text-white" : "bg-[var(--surface)] text-[var(--text-muted)] hover:text-white"
            }`}
          >
            <Mail size={14} /> Mesajlar ({messages.length}{unreadCount > 0 ? ` · ${unreadCount} yeni` : ""})
          </button>
          <button
            onClick={() => setTab("translations")}
            data-testid="tab-translations"
            className={`px-4 md:px-6 py-3 text-xs uppercase tracking-widest flex items-center gap-2 ${
              tab === "translations" ? "bg-[var(--bg)] text-white" : "bg-[var(--surface)] text-[var(--text-muted)] hover:text-white"
            }`}
          >
            <Languages size={14} /> Çeviriler
          </button>
          <button
            onClick={() => setTab("content")}
            data-testid="tab-content"
            className={`px-4 md:px-6 py-3 text-xs uppercase tracking-widest flex items-center gap-2 ${
              tab === "content" ? "bg-[var(--bg)] text-white" : "bg-[var(--surface)] text-[var(--text-muted)] hover:text-white"
            }`}
          >
            <FileText size={14} /> İçerik
          </button>
        </div>
      </header>

      <main className="max-w-[1440px] mx-auto px-6 lg:px-10 py-10">
        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin text-white" /></div>
        ) : tab === "projects" ? (
          <div data-testid="projects-panel">
            <div className="flex items-start justify-between mb-2 flex-wrap gap-3">
              <div>
                <h2 className="font-display text-2xl text-white">Projeler</h2>
                <p className="text-sm text-[var(--text-muted)] mt-1">Sitenin projeler bölümündeki tüm projeler. Yeni ekle, düzenle veya sil.</p>
              </div>
              <button onClick={openNew} className="btn-sharp btn-primary !py-3 !px-5 !text-xs" data-testid="add-project-button">
                <Plus size={14} /> Yeni Proje
              </button>
            </div>
            <div className="my-6 h-px bg-[var(--border)]" />

            {projects.length === 0 ? (
              <div className="py-24 text-center text-[var(--text-muted)]">Henüz proje yok</div>
            ) : (
              <div className="border border-[var(--border)] overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--surface)] text-[var(--text-muted)] text-xs uppercase tracking-widest">
                    <tr>
                      <th className="text-left p-4">{t.admin.title}</th>
                      <th className="text-left p-4 hidden md:table-cell">{t.admin.category}</th>
                      <th className="text-left p-4 hidden lg:table-cell">{t.admin.location}</th>
                      <th className="text-left p-4 hidden lg:table-cell">{t.admin.year}</th>
                      <th className="p-4 w-32"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map((p, idx) => (
                      <tr key={p.id} className="border-t border-[var(--border)] hover:bg-[var(--surface)]" data-testid={`row-project-${p.id}`}>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            {p.image_url ? (
                              <img src={p.image_url} alt="" className="w-12 h-12 object-cover" />
                            ) : (
                              <div className="w-12 h-12 bg-white/10 flex items-center justify-center shrink-0">
                                <ImageIcon size={16} className="text-white/30" />
                              </div>
                            )}
                            <div>
                              <div className="text-white font-medium">{p.title}</div>
                              {p.featured && <div className="text-[10px] uppercase tracking-widest text-[var(--silver)] mt-0.5">Featured</div>}
                            </div>
                          </div>
                        </td>
                        <td className="p-4 text-[var(--text-muted)] hidden md:table-cell">{p.category}</td>
                        <td className="p-4 text-[var(--text-muted)] hidden lg:table-cell">{p.location}</td>
                        <td className="p-4 text-[var(--text-muted)] hidden lg:table-cell">{p.year}</td>
                        <td className="p-4">
                          <div className="flex gap-2 justify-end">
                            <button
                              onClick={() => moveProject(idx, -1)}
                              disabled={idx === 0 || reordering}
                              className="p-2 border border-[var(--border)] text-white hover:border-white disabled:opacity-30 disabled:cursor-not-allowed"
                              data-testid={`move-up-${p.id}`}
                              title="Yukarı taşı"
                            >↑</button>
                            <button
                              onClick={() => moveProject(idx, 1)}
                              disabled={idx === projects.length - 1 || reordering}
                              className="p-2 border border-[var(--border)] text-white hover:border-white disabled:opacity-30 disabled:cursor-not-allowed"
                              data-testid={`move-down-${p.id}`}
                              title="Aşağı taşı"
                            >↓</button>
                            <button onClick={() => openEdit(p)} className="p-2 border border-[var(--border)] text-white hover:border-white" data-testid={`edit-${p.id}`}>
                              <Pencil size={14} />
                            </button>
                            <button onClick={() => onDelete(p)} className="p-2 border border-[var(--border)] text-white hover:border-red-500 hover:text-red-400" data-testid={`delete-${p.id}`}>
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : tab === "hero" ? (
          <div data-testid="hero-panel">
            <div className="flex items-start justify-between mb-2 flex-wrap gap-3">
              <div>
                <h2 className="font-display text-2xl text-white">Anasayfa Görselleri</h2>
                <p className="text-sm text-[var(--text-muted)] mt-1 max-w-2xl">
                  Anasayfanın üst kısmında "Yarınlara Değer Katan Projeler" yazısının arkasında dönen slayt görselleri. <strong className="text-white">İlk görsel</strong> sayfa açılışında ilk görünen olur. Sıralamayı yukarı/aşağı oklarla değiştirebilirsiniz.
                </p>
              </div>
              <div className="flex gap-2">
                <input ref={heroUploadRef} type="file" accept="image/*" multiple onChange={uploadHeroSlide} className="hidden" data-testid="hero-upload-file" />
                <button onClick={() => heroUploadRef.current?.click()} disabled={uploading}
                  className="btn-sharp text-white !py-3 !px-4 !text-xs disabled:opacity-50" data-testid="hero-upload-button">
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  Görsel Yükle
                </button>
                <button onClick={addHeroSlide} className="btn-sharp text-white !py-3 !px-4 !text-xs" data-testid="hero-add-url">
                  <Plus size={14} /> URL Ekle
                </button>
                <button onClick={saveHero} disabled={heroSaving}
                  className="btn-sharp btn-primary !py-3 !px-5 !text-xs disabled:opacity-50" data-testid="hero-save-button">
                  {heroSaving && <Loader2 size={14} className="animate-spin" />}
                  Kaydet
                </button>
              </div>
            </div>
            <div className="my-6 h-px bg-[var(--border)]" />

            {heroSlides.length === 0 ? (
              <div className="py-24 text-center text-[var(--text-muted)]" data-testid="hero-empty">
                Henüz slayt eklenmedi. Yukarıdan "Görsel Yükle" ya da "URL Ekle" ile başlayın.
              </div>
            ) : (
              <div className="space-y-3">
                {heroSlides.map((src, i) => (
                  <div key={i} className="flex items-center gap-4 p-3 border border-[var(--border)] bg-[var(--surface)]" data-testid={`hero-slide-${i}`}>
                    <div className="w-8 text-center text-xs tracking-widest text-[var(--text-muted)]">
                      {i + 1}
                      {i === 0 && <div className="text-[8px] text-[var(--primary)] mt-1">İLK</div>}
                    </div>
                    <div className="w-24 h-16 bg-black shrink-0 overflow-hidden border border-[var(--border)]">
                      {src ? <img src={src} alt={`Slayt ${i + 1}`} className="w-full h-full object-cover" /> : (
                        <div className="w-full h-full flex items-center justify-center text-[10px] text-[var(--text-muted)]">boş</div>
                      )}
                    </div>
                    <input
                      value={src}
                      onChange={(e) => updateHeroSlide(i, e.target.value)}
                      placeholder="https://... veya /api/uploads/..."
                      className="input-sharp flex-1 font-mono text-xs"
                      data-testid={`hero-input-${i}`}
                    />
                    <div className="flex flex-col gap-1">
                      <button onClick={() => moveSlide(i, -1)} disabled={i === 0}
                        className="p-1.5 border border-[var(--border)] text-white hover:border-white disabled:opacity-30"
                        data-testid={`hero-up-${i}`} title="Yukarı taşı">
                        ▲
                      </button>
                      <button onClick={() => moveSlide(i, 1)} disabled={i === heroSlides.length - 1}
                        className="p-1.5 border border-[var(--border)] text-white hover:border-white disabled:opacity-30"
                        data-testid={`hero-down-${i}`} title="Aşağı taşı">
                        ▼
                      </button>
                    </div>
                    <button onClick={() => removeHeroSlide(i)}
                      className="p-2 border border-[var(--border)] text-white hover:border-red-500 hover:text-red-400"
                      data-testid={`hero-remove-${i}`} title="Sil">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : tab === "messages" ? (
          <div data-testid="messages-panel">
            <div className="mb-2">
              <h2 className="font-display text-2xl text-white">Mesajlar</h2>
              <p className="text-sm text-[var(--text-muted)] mt-1">İletişim formundan gelen müşteri mesajları.</p>
            </div>
            <div className="my-6 h-px bg-[var(--border)]" />
            {messages.length === 0 ? (
              <div className="py-24 text-center text-[var(--text-muted)]">Henüz mesaj yok</div>
            ) : (
              <div className="space-y-3">
                {messages.map((m) => (
                  <div
                    key={m.id}
                    className={`border p-6 ${m.read ? "border-[var(--border)] bg-[var(--surface)]" : "border-white/40 bg-[var(--surface)]"}`}
                    data-testid={`msg-${m.id}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                      <div>
                        <div className="text-white font-medium flex items-center gap-2">
                          {m.name}
                          {!m.read && <span className="text-[10px] uppercase tracking-widest bg-white text-black px-2 py-0.5">{t.admin.unread}</span>}
                        </div>
                        <div className="text-xs text-[var(--text-muted)] mt-1">
                          {m.phone} {m.email ? `· ${m.email}` : ""} · {m.service_type}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {!m.read && (
                          <button onClick={() => onMarkRead(m)} className="p-2 border border-[var(--border)] text-white hover:border-white" data-testid={`read-${m.id}`}>
                            <CheckCircle2 size={14} />
                          </button>
                        )}
                        <button onClick={() => onDeleteMsg(m)} className="p-2 border border-[var(--border)] text-white hover:border-red-500" data-testid={`del-msg-${m.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-[var(--text-main)] whitespace-pre-wrap">{m.message}</p>
                    <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)] mt-4">
                      {new Date(m.created_at).toLocaleString("tr-TR")}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : tab === "translations" ? (
          <ProjectTranslations projects={projects} onSaved={load} />
        ) : tab === "content" ? (
          <ContentManager />
        ) : null}
      </main>

      {/* Edit Modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="project-modal">
          <form
            onSubmit={onSave}
            className="w-full max-w-2xl bg-[var(--surface)] border border-[var(--border)] p-8 my-12 space-y-4"
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display text-2xl text-white">
                {editing.new ? t.admin.add : t.admin.edit}
              </h3>
              <button type="button" onClick={close} className="p-2 text-white hover:text-[var(--silver)]" data-testid="close-modal">
                <X size={18} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-[var(--text-muted)] mb-2 block">{t.admin.title} *</label>
                <input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="input-sharp" required data-testid="form-title" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[var(--text-muted)] mb-2 block">{t.admin.titleEn}</label>
                <input value={form.title_en} onChange={(e) => setForm({...form, title_en: e.target.value})} className="input-sharp" data-testid="form-title-en" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[var(--text-muted)] mb-2 block">{t.admin.category} *</label>
                <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="input-sharp" data-testid="form-category">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[var(--text-muted)] mb-2 block">{t.admin.status} *</label>
                <select value={form.status || "Tamamlanan"} onChange={(e) => setForm({...form, status: e.target.value})} className="input-sharp" data-testid="form-status">
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[var(--text-muted)] mb-2 block">{t.admin.location} *</label>
                <input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} className="input-sharp" required data-testid="form-location" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[var(--text-muted)] mb-2 block">{t.admin.year}</label>
                <input value={form.year} onChange={(e) => setForm({...form, year: e.target.value})} className="input-sharp" data-testid="form-year" />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-3 cursor-pointer text-sm text-white">
                  <input
                    type="checkbox"
                    checked={!!form.featured}
                    onChange={(e) => setForm({...form, featured: e.target.checked})}
                    className="w-5 h-5 accent-white"
                    data-testid="form-featured"
                  />
                  {t.admin.featured}
                </label>
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest text-[var(--text-muted)] mb-2 block">{t.admin.imageUrl} *</label>
              <div className="flex gap-2 items-stretch">
                <input value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})} className="input-sharp flex-1" data-testid="form-image" placeholder="https://... veya yükle →" />
                <input ref={coverInputRef} type="file" accept="image/*" onChange={onUploadCover} className="hidden" data-testid="form-image-file" />
                <button type="button" onClick={() => coverInputRef.current?.click()} disabled={uploading}
                  className="px-4 border border-[var(--border)] text-white hover:border-white text-xs tracking-widest uppercase flex items-center gap-2 disabled:opacity-50"
                  data-testid="upload-cover-button">
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  Yükle
                </button>
                <button type="button" onClick={() => setLibTarget("cover")}
                  className="px-4 border border-[var(--border)] text-white hover:border-white text-xs tracking-widest uppercase flex items-center gap-2"
                  data-testid="library-cover-button">
                  <ImageIcon size={14} />
                  Kütüphane
                </button>
              </div>
              {form.image_url && (
                <div className="mt-3 inline-block border border-[var(--border)]" data-testid="cover-preview">
                  <img src={form.image_url} alt="Kapak önizleme" className="h-24 w-auto object-cover" />
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs uppercase tracking-widest text-[var(--text-muted)]">{t.admin.gallery}</label>
                <div className="flex gap-2">
                  <input ref={galleryInputRef} type="file" accept="image/*" multiple onChange={onUploadGallery} className="hidden" data-testid="form-gallery-file" />
                  <button type="button" onClick={() => galleryInputRef.current?.click()} disabled={uploading}
                    className="px-3 py-1.5 border border-[var(--border)] text-white hover:border-white text-[10px] tracking-widest uppercase flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="upload-gallery-button">
                    {uploading ? <Loader2 size={12} className="animate-spin" /> : <ImageIcon size={12} />}
                    Çoklu Görsel Yükle
                  </button>
                  <button type="button" onClick={() => setLibTarget("gallery")}
                    className="px-3 py-1.5 border border-[var(--border)] text-white hover:border-white text-[10px] tracking-widest uppercase flex items-center gap-1.5"
                    data-testid="library-gallery-button">
                    <ImageIcon size={12} />
                    Kütüphane
                  </button>
                </div>
              </div>
              <textarea
                value={form.gallery}
                onChange={(e) => setForm({...form, gallery: e.target.value})}
                rows={4}
                placeholder="https://...&#10;https://...&#10;Veya yukarıdan çoklu görsel yükleyin"
                className="input-sharp resize-none font-mono text-xs"
                data-testid="form-gallery"
              />
              {String(form.gallery || "").split("\n").map((s) => s.trim()).filter(Boolean).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="gallery-preview">
                  {String(form.gallery).split("\n").map((s) => s.trim()).filter(Boolean).map((src, i) => (
                    <div key={i} className="relative border border-[var(--border)]">
                      <img src={src} alt={`Galeri ${i+1}`} className="h-16 w-24 object-cover" />
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[var(--text-muted)] mb-2 block">
                YouTube Linkleri (her satıra bir URL)
              </label>
              <textarea
                value={form.youtube_urls}
                onChange={(e) => setForm({...form, youtube_urls: e.target.value})}
                rows={3}
                placeholder="https://www.youtube.com/watch?v=...&#10;https://youtu.be/..."
                className="input-sharp resize-none font-mono text-xs"
                data-testid="form-youtube"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[var(--text-muted)] mb-2 block">{t.admin.description}</label>
              <textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={3} className="input-sharp resize-none" data-testid="form-desc" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[var(--text-muted)] mb-2 block">{t.admin.descriptionEn}</label>
              <textarea value={form.description_en} onChange={(e) => setForm({...form, description_en: e.target.value})} rows={3} className="input-sharp resize-none" data-testid="form-desc-en" />
            </div>

            <div className="flex gap-3 justify-end pt-4">
              <button type="button" onClick={close} className="btn-sharp text-white" data-testid="modal-cancel">
                {t.admin.cancel}
              </button>
              <button type="submit" disabled={saving} className="btn-sharp btn-primary disabled:opacity-60" data-testid="modal-save">
                {saving && <Loader2 size={14} className="animate-spin" />}
                {t.admin.save}
              </button>
            </div>
          </form>
        </div>
      )}
      {editing && (
        <MediaLibrary open={!!libTarget} multi={libTarget === "gallery"} onClose={() => setLibTarget(null)} onSelect={onLibrarySelect} />
      )}
    </div>
  );
}
